'use client';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { useServerInsertedHTML } from 'next/navigation';
import { ReactNode, useState } from 'react';

interface EmotionCacheProviderProps {
  children: ReactNode;
  nonce?: string;
}

/**
 * Aligns Emotion SSR output with the client so Chakra styled primitives hydrate cleanly
 * (see Chakra Next.js guide — style vs. div mismatch with App Router / bundlers).
 */
export function EmotionCacheProvider({ children, nonce }: EmotionCacheProviderProps) {
  const [{ cache, flush }] = useState(() => {
    const nextCache = createCache({ key: 'chakra', prepend: true, nonce });
    nextCache.compat = true;
    const originalInsert = nextCache.insert;
    let insertedNames: string[] = [];

    nextCache.insert = (...args) => {
      const serialized = args[1];
      if (nextCache.inserted[serialized.name] === undefined) {
        insertedNames.push(serialized.name);
      }
      return originalInsert(...args);
    };

    return {
      cache: nextCache,
      flush() {
        const names = insertedNames;
        insertedNames = [];
        return names;
      },
    };
  });

  useServerInsertedHTML(() => {
    const names = flush();
    if (names.length === 0) {
      return null;
    }
    let styles = '';
    for (const name of names) {
      const value = cache.inserted[name];
      if (typeof value === 'string') {
        styles += value;
      }
    }
    if (!styles) {
      return null;
    }
    return (
      <style
        data-emotion={`${cache.key} ${names.join(' ')}`}
        nonce={nonce}
        dangerouslySetInnerHTML={{ __html: styles }}
      />
    );
  });

  return <CacheProvider value={cache}>{children}</CacheProvider>;
}
